import BaseVisualizationService from "@configuratorware/configurator-frontendgui/App/Services/VisualizationService";
import { THREE } from "redhotmagma-visualization/ExposedModules";
import { createWorkingCanvas } from "redhotmagma-visualization/Utils/createCanvas";
import { EventTypes } from "redhotmagma-visualization/Constants";

// double sized texture makes the texts sharp for higher zoom values
const drawingTextureScale = 1;

// the product image can be zoomed up to this value to fill the canvas
const maxZoom = 2;

export default class VisualizationService extends BaseVisualizationService {
  setComponent(component) {
    /**
     * Replace the _getDrawingContext function to work with the drawingTarget object
     * (note: the original version depends on design areas only used for designer products)
     * @param {function} drawingFn
     * @return {Promise<void>}
     * @private
     */
    component._getDrawingContext = async function (drawingFn) {
      if (!this.drawingTarget) {
        return;
      }
      const { ctx, canvas, texture } = this.getCanvasContextForTexture(
        this.drawingTarget.material.map
      );
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);
      await drawingFn(ctx, { width, height, ratio: drawingTextureScale });
      texture.needsUpdate = true;
      this.render();
    };

    // store the original initModelWithTexture (so it can be called from the replacement function)
    const { initModelWithTexture } = component;

    /**
     * Extend the initModelWithTexture function with creating the drawingTarget object
     * @param args
     */
    component.initModelWithTexture = function (...args) {
      initModelWithTexture.apply(this, args);

      const { width, height } = this._model.material.map.image;

      const [canvas] = createWorkingCanvas({
        width: width * drawingTextureScale,
        height: height * drawingTextureScale,
      });

      const texture = new THREE.Texture(canvas);
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
      });

      const geometry = new THREE.PlaneGeometry(width, height, 1, 1);
      const plane = new THREE.Mesh(geometry, material);
      plane.position.z = 1;

      this._model.add(plane);
      this.drawingTarget = plane;
    };

    component.observable.on(EventTypes.Initialized, () => {
      component.toggleCameraLock(true);
    });

    component.getDefaultZoom = function getDefaultZoom() {
      const size = new THREE.Vector2();
      this.renderer.getSize(size);
      return this._size
        ? this.getZoomForSize(size)
        : 1;
    };

    component.getZoomForSize = function getZoomForSize(targetSize) {
      const { width: tWidth, height: tHeight } = targetSize;
      const { width, height } = this._size;
      return Math.min(tWidth / width, tHeight / height, maxZoom);
    }

    return super.setComponent(component);
  }
}
